import Countdown, { zeroPad } from 'react-countdown';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Dropdown, Tag } from 'antd';
import { CheckOutlined, ClockCircleOutlined, MoreOutlined, WarningOutlined } from '@ant-design/icons';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import OrderLineItemsList from '../../components/alert-screens/shared/order-line-item/OrderLineItemsList';
import { useHelpers } from '../../providers/HelpersProvider';
import OrderPaymentBreakdown from '../../components/alert-screens/shared/OrderPaymentBreakdown';
import { useSelector } from 'react-redux';
import { DeliveryMode, OrderFulfillmentStatus, OrderMethod } from '../../services/exports/Constants';
import { ReceiptType } from '../../providers/PrintProvider';
import OrderFulfillmentsManager from '../../services/api/OrderFulfillmentsManager';
import OrdersManager from '../../services/api/OrdersManager';
import useFeedbackHandler from '../../hooks/useFeedbackHandler';
import usePrinter from '../../hooks/usePrinter';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import ReactDOM from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import classnames from 'classnames';
import PrimaryOrderBadge from '../../components/badge/PrimaryOrderBadge';
import { useTranslation } from 'react-i18next';
import useSound from '../../hooks/utility/useSound';
import OrderSummary from '../../components/alert-screens/shared/OrderSummary';
import OrderMethodIcon, { Sizes } from '../../components/order/OrderMethodIcon';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import OrderTimeChangeInput from '../../components/order/OrderTimeChangeInput';
import OrderPreparationTimeOptions from '../../components/order/OrderPreparationTimeOptions';
import TableBox, { TableBoxVariant } from '../../components/order/TableBox';
import useTableLabel from '../../hooks/useTableLabel';

export const Step = {
  OrderPreview: 'order_preview',
  OrderConfirmation: 'order_confirmation',
};

export const TimeConfirmationStep = {
  TimeConfirmation: 'time_confirmation',
  TimeChange: 'time_change',
};

export const TimeConfirmationVariant = {
  TimeInput: 'time_input',
  TimeOptions: 'time_options',
};

const PendingOrderAlertScreen = ({ order }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:PendingOrderAlertScreen' });

  const { playSound } = useSound();
  const { print, setPrinting } = usePrinter();
  const { setFeedback } = useFeedbackHandler({ renderToasts: true });
  const { replaceOrderData } = useOngoingOrders();

  const { company } = useSelector((state) => state.company);
  const [activeStep, setActiveStep] = useState(Step.OrderPreview);
  const [preparationTime, setPreparationTimeState] = useState(order.estimated_preparation_time);
  const preparationTimeTouchedRef = useRef(false);

  const setPreparationTime = (value) => {
    preparationTimeTouchedRef.current = true;
    setPreparationTimeState(value);
  };

  // The order prop can update via websocket after mount (e.g. the backend finalizes
  // a pre-order's `estimated_preparation_time` once the restaurant opens). Without
  // this sync, the initial state captured at mount becomes stale and gets PATCHed
  // back on manual confirmation, clobbering the scheduled pickup time.
  useEffect(() => {
    if (!preparationTimeTouchedRef.current) {
      setPreparationTimeState(order.estimated_preparation_time);
    }
  }, [order.estimated_preparation_time]);

  useEffect(() => {
    playSound();
    const id = setInterval(() => playSound(), 10000);

    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    handleUnreviewedOrder();
  }, []);

  async function handleUnreviewedOrder() {
    if (order.fulfillment_status !== OrderFulfillmentStatus.Created) {
      return null;
    }

    const { success, data } = await OrderFulfillmentsManager.markReviewed(order.fulfillment_id);

    if (!success) {
      return setFeedback({
        message: t('error_messages.something_went_wrong'),
        type: 'error',
      });
    }

    data?.data && replaceOrderData(data.data);
  }

  async function acceptOrder(extra = {}) {
    const { success, status, data } = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      status:
        order.fulfillment_status === OrderFulfillmentStatus.Created ||
        order.fulfillment_status === OrderFulfillmentStatus.Reviewed
          ? OrderFulfillmentStatus.Accepted
          : order.status,
      is_manually_confirmed: true,
      ...extra,
    });

    if (!success) {
      return showSomethingWentWrong(
        (status === 412 && data?.error_code && t(`steps.${Step.OrderConfirmation}.toasts.${data.error_code}`)) ||
          data?.message,
      );
    }

    // On a flaky tablet connection a 2xx can arrive without its body (axios then hands back
    // a string). The Accept was most likely applied, so reload the order instead of guessing.
    const acceptedOrder = data?.data ?? (await reloadAcceptedOrder());

    if (!acceptedOrder) {
      return showSomethingWentWrong();
    }

    !acceptedOrder.is_pickup_time_optimized &&
      toast(
        <div className="flex gap-sm">
          <WarningOutlined />
          <div className="flex flex-col">
            <span className="w-full font-semibold">
              {t(`steps.${Step.OrderConfirmation}.toasts.order_confirmed.title`, { number: acceptedOrder.number })}
            </span>
            <span>{t(`steps.${Step.OrderConfirmation}.toasts.order_confirmed.body`)}</span>
          </div>
        </div>,
        { className: 'p-sm !rounded-lg !mt-sm' },
      );
    replaceOrderData(acceptedOrder);

    if (acceptedOrder.is_pickup_time_optimized && company?.auto_print) {
      setPrinting(true);
      print({ type: ReceiptType.Order, payload: acceptedOrder }, true);
    }
  }

  async function reloadAcceptedOrder() {
    const { success, data } = await OrdersManager.show(order.id);
    const reloadedOrder = success ? data?.data : null;

    if (!reloadedOrder) {
      return null;
    }

    if (
      reloadedOrder.fulfillment_status === OrderFulfillmentStatus.Created ||
      reloadedOrder.fulfillment_status === OrderFulfillmentStatus.Reviewed
    ) {
      replaceOrderData(reloadedOrder);

      return null;
    }

    return reloadedOrder;
  }

  function showSomethingWentWrong(message) {
    return setFeedback({
      type: 'error',
      message: message || t('error_messages.something_went_wrong'),
    });
  }

  const props = {
    order,
    preparationTime,
    setPreparationTime,
    setActiveStep,
    acceptOrder,
  };

  return ReactDOM.createPortal(
    <RemoveScroll
      enabled
      className={classnames('w-full inset-0 fixed z-[1200] flex justify-center', {
        'lg:p-md p-xs': activeStep === Step.OrderPreview,
        'bg-primary': activeStep === Step.OrderPreview && !order.scheduled_for,
        'bg-blue-500': activeStep === Step.OrderPreview && !!order.scheduled_for,
        'bg-black/40': activeStep === Step.OrderConfirmation,
      })}
    >
      {activeStep === Step.OrderPreview && <PendingOrderAlertScreen.OrderPreview {...props} />}
      {activeStep === Step.OrderConfirmation && <PendingOrderAlertScreen.OrderConfirmation {...props} />}
    </RemoveScroll>,
    document.body,
  );
};

export default PendingOrderAlertScreen;

PendingOrderAlertScreen.OrderPreview = ({ order, preparationTime, setPreparationTime, setActiveStep, acceptOrder }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:PendingOrderAlertScreen' });
  const { t: tShared } = useTranslation(undefined, { keyPrefix: 'Shared:Constants:OrderMethods' });
  const tableLabel = useTableLabel();
  const method = OrderHelpers.copyMethod(order);
  const isOnSite = method === OrderMethod.OnSite;

  const { removeOrder } = useOngoingOrders();
  const { printing } = usePrinter();
  const { formatTime } = useHelpers();

  const { company } = useSelector((state) => state.company);
  const [loading, setLoading] = useState(false);
  const pickupEstimate = OrderHelpers.getMinPreparationTime(order, company) ?? order.estimated_preparation_time;
  const isExternalDelivery = OrderHelpers.isExternalDelivery(order);
  const isToday = useMemo(
    () => DateTime.fromISO(order.estimated_pickup_time).hasSame(DateTime.local(), 'day'),
    [order.estimated_pickup_time],
  );

  // "today" has its own phrase: German can't say „am heute um 12:30".
  const timeTagVariant = order.scheduled_for ? (isToday ? 'scheduled_today' : 'scheduled') : 'asap';

  const hasTwoStepConfirmation = company?.delivery_mode === DeliveryMode.External || isExternalDelivery;

  const onConfirm = () => {
    if (order.scheduled_for && isExternalDelivery) {
      setLoading(true);

      return acceptOrder().finally(() => setLoading(false));
    }

    if (hasTwoStepConfirmation) {
      return setActiveStep(Step.OrderConfirmation);
    }

    setLoading(true);
    acceptOrder({ estimated_preparation_time: preparationTime }).finally(() => setLoading(false));
  };

  return (
    <div className="max-h-full relative m-auto bg-white w-full p-md space-y-md lg:w-3/5 rounded-lg flex flex-col">
      <PendingOrderAlertScreen.RejectOrderMenu order={order} />
      <div className="mx-auto w-fit flex items-center">
        <OrderMethodIcon order={order} className="my-auto" size={Sizes.xl} />
      </div>
      <div className="flex-1 overflow-y-auto space-y-sm">
        <div className="flex flex-col justify-center space-y-sm">
          <h4
            className={classnames('mx-auto uppercase font-bold my-auto text-center', {
              'text-primary': !order.scheduled_for,
              'text-blue-500': !!order.scheduled_for,
            })}
          >
            {isOnSite
              ? t(`steps.${Step.OrderPreview}.titles.${order.scheduled_for ? 'on_site_scheduled' : 'on_site'}`)
              : t(
                  order.scheduled_for
                    ? `steps.${Step.OrderPreview}.titles.scheduled`
                    : `steps.${Step.OrderPreview}.titles.asap`,
                  { method: tShared(method) },
                )}
          </h4>
          {isOnSite && <TableBox variant={TableBoxVariant.Alert} label={tableLabel(order)} />}
          <Tag
            className={classnames(
              'w-fit mx-auto py-xs px-sm border rounded-2xl text-sm font-medium whitespace-normal text-center',
              {
                'border text-gray-500': !order.scheduled_for,
                'text-blue-500 border-blue-500/25 bg-blue-500/10': !!order.scheduled_for,
              },
            )}
          >
            {isExternalDelivery &&
              t(
                order.scheduled_for
                  ? `steps.${Step.OrderPreview}.time_tag_estimated.scheduled`
                  : `steps.${Step.OrderPreview}.time_tag_estimated.asap`,
                {
                  day: isToday
                    ? t(`steps.${Step.OrderPreview}.labels.today`)
                    : DateTime.fromISO(order.estimated_pickup_time).toFormat('cccc, dd LLL'),
                  time: order.scheduled_for
                    ? formatTime(order.estimated_pickup_time)
                    : `${pickupEstimate}-${pickupEstimate + 10} min`,
                },
              )}
            {!isExternalDelivery && (
              <>
                {/* The design's chip for table orders: a clock icon, not the 🕓 in the copy. */}
                {isOnSite && <ClockCircleOutlined aria-hidden className="mr-2xs" />}
                {t(`steps.${Step.OrderPreview}.time_tag.${method}.${timeTagVariant}`, {
                  day: DateTime.fromISO(order.estimated_pickup_time).toFormat('cccc, dd LLL'),
                  time: order.scheduled_for
                    ? formatTime(order.estimated_pickup_time)
                    : `${pickupEstimate}-${pickupEstimate + 10} min`,
                  // The table-order copy formats the range itself ("10–20 Min.").
                  from: pickupEstimate,
                  to: pickupEstimate + 10,
                })}
              </>
            )}
          </Tag>
          {!order.is_confirmed && (
            <Alert
              type="error"
              title={
                <h6 className="text-danger flex gap-xs">
                  <ClockCircleOutlined />
                  <Countdown
                    key={order.id}
                    date={DateTime.fromISO(order.auto_rejects_at).toJSDate()}
                    onComplete={() => removeOrder(order.id)}
                    renderer={({ minutes, seconds }) => (
                      <span>
                        {minutes}:{zeroPad(seconds)}
                      </span>
                    )}
                  />
                  <span className="text-xs">{t(`steps.${Step.OrderPreview}.labels.time_remaining_to_accept`)}</span>
                </h6>
              }
              className="w-fit mx-auto"
            />
          )}
        </div>
        {isExternalDelivery && (
          <Alert
            showIcon
            type="warning"
            icon={<WarningOutlined />}
            title={t(`steps.${Step.OrderPreview}.feedback.rough_estimate_warning`)}
            className="mt-md"
          />
        )}
        <OrderSummary order={order} />
        <OrderPaymentBreakdown order={order} />
        <OrderLineItemsList order={order} />
      </div>
      <div className="sticky bottom-0">
        {!hasTwoStepConfirmation && (
          <OrderTimeChangeInput
            order={order}
            preparationTime={preparationTime}
            setPreparationTime={setPreparationTime}
          />
        )}
        <Button
          type="primary"
          icon={<CheckOutlined />}
          loading={loading || printing}
          onClick={onConfirm}
          className={classnames('w-full uppercase py-sm mt-md', {
            '!bg-blue-500 !border-blue-500 hover:!bg-blue-500 hover:!border-blue-500 active:!bg-blue-500 active:!border-blue-500':
              !!order.scheduled_for,
          })}
          size="large"
        >
          {t(`steps.${Step.OrderPreview}.buttons.confirm_order`)}
        </Button>
      </div>
    </div>
  );
};

PendingOrderAlertScreen.OrderConfirmation = ({ order, preparationTime, setPreparationTime, acceptOrder }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:PendingOrderAlertScreen' });
  const variant = order.scheduled_for ? TimeConfirmationVariant.TimeInput : TimeConfirmationVariant.TimeOptions;

  const { formatTime } = useHelpers();
  const { printing } = usePrinter();

  const { company } = useSelector((state) => state.company);
  const pickupEstimate = OrderHelpers.getMinPreparationTime(order, company) ?? order.estimated_preparation_time;
  const [step, setStep] = useState(TimeConfirmationStep.TimeConfirmation);
  const [loading, setLoading] = useState(false);
  const [selectedTimeValid, setSelectedTimeValid] = useState(true);

  const pickupTime = DateTime.fromISO(order.estimated_pickup_time);
  const isToday = useMemo(
    () => DateTime.fromISO(order.estimated_pickup_time).hasSame(DateTime.local(), 'day'),
    [order.estimated_pickup_time],
  );

  const isOrderAcceptable =
    order.fulfillment_status === OrderFulfillmentStatus.Created ||
    order.fulfillment_status === OrderFulfillmentStatus.Reviewed ||
    order.fulfillment_status === OrderFulfillmentStatus.Accepted;

  async function _acceptOrder() {
    setLoading(true);
    acceptOrder({ estimated_preparation_time: preparationTime }).finally(() => setLoading(false));
  }

  const Component = {
    [TimeConfirmationVariant.TimeInput]: OrderTimeChangeInput,
    [TimeConfirmationVariant.TimeOptions]: OrderPreparationTimeOptions,
  }[variant];

  const props = {
    order,
    preparationTime,
    setPreparationTime,
    setSelectedTimeValid,
  };

  const timeLabel = t(
    `steps.${Step.OrderConfirmation}.labels.order_time.${OrderHelpers.copyMethod(order)}.${order.scheduled_for ? (isToday ? 'scheduled_today' : 'scheduled') : 'asap'}`,
  );

  return (
    <div className="max-h-full relative bg-white m-auto w-full p-md space-y-md lg:w-2/5 rounded-lg flex flex-col">
      <PendingOrderAlertScreen.RejectOrderMenu order={order} />
      <div className="bg-black rounded-full flex justify-center items-center p-md w-fit mx-auto">
        <ClockCircleOutlined className="text-md text-white" />
      </div>
      <div className="space-y-sm">
        <h6 className="w-full text-center">{t(`steps.${Step.OrderConfirmation}.title`)}</h6>
        <div className="flex flex-col space-y-xs">
          {step === TimeConfirmationStep.TimeConfirmation && (
            <>
              <span className="w-full text-center flex justify-center">
                <span className="my-auto">{timeLabel}</span>
                <PrimaryOrderBadge order={order} className="ml-xs my-auto">
                  {order.scheduled_for ? (
                    <>
                      {isToday
                        ? t(`steps.${Step.OrderConfirmation}.labels.today`)
                        : pickupTime.toFormat('cccc, dd LLL')}
                      {', '}
                      {formatTime(order.estimated_pickup_time)}
                    </>
                  ) : (
                    `${pickupEstimate} min`
                  )}
                </PrimaryOrderBadge>
              </span>
              <span className="w-full text-center text-sm font-medium">
                {t(`steps.${Step.OrderConfirmation}.labels.can_you_make_it`)}
              </span>
            </>
          )}
          <span className="w-full text-center text-xs text-gray-500">
            {t(`steps.${Step.OrderConfirmation}.labels.we_also_send_this_update_to_the_customer`)}
          </span>
          {step === TimeConfirmationStep.TimeChange && <Component {...props} />}
        </div>
        <Button
          type="primary"
          onClick={_acceptOrder}
          disabled={!selectedTimeValid || !isOrderAcceptable}
          loading={loading || printing}
          className="w-full"
          size="large"
        >
          {step === TimeConfirmationStep.TimeConfirmation
            ? t(`steps.${Step.OrderConfirmation}.buttons.yes_lets_go`)
            : t(`steps.${Step.OrderConfirmation}.buttons.confirm`)}
        </Button>
        {step === TimeConfirmationStep.TimeConfirmation && (
          <Button onClick={() => setStep(TimeConfirmationStep.TimeChange)} className="w-full" size="large">
            {t(`steps.${Step.OrderConfirmation}.buttons.no_change_time`)}
          </Button>
        )}
      </div>
    </div>
  );
};

PendingOrderAlertScreen.RejectOrderMenu = ({ order }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:PendingOrderAlertScreen' });

  const { toggleRejectOrderModal } = useOngoingOrders();

  return (
    <div className="absolute right-md top-md">
      <Dropdown
        getPopupContainer={(t) => t.parentElement.parentElement}
        menu={{
          items: [
            {
              key: '1',
              label: t(`steps.${Step.OrderPreview}.buttons.cancel_order`),
              onClick: () => toggleRejectOrderModal(order),
            },
          ],
        }}
      >
        <Button icon={<MoreOutlined />} type="text" />
      </Dropdown>
    </div>
  );
};
